import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { CompanySidebar } from '../components/Dashboard/CompanySidebar';
import JSZip from 'jszip';
import {
    Filter,
    Upload,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    MapPin,
    Briefcase,
    GraduationCap,
    Star,
    ExternalLink,
    Mail,
    Loader2,
    Users,
    CheckCircle,
    AlertCircle,
    X,
    Download,
    Linkedin,
    Rocket,
    Building2,
    Lightbulb,
    FileText,
    TrendingUp,
    Copy,
    Info,
    Check,
    Award,
    Lock,
    Unlock,
    Sparkles,
    Flame,
    CloudUpload,
    Trash2,
    FolderOpen
} from 'lucide-react';

// Types for the candidates page
interface HiringJob {
    id: string;
    job_title: string;
    job_description: string;
    created_at: string;
}

interface CompanyHistory {
    name: string;
    role: string;
    tier?: string;
    start_year?: number;
    end_year?: number; // null/undefined means "Present"
}

interface EducationHistory {
    uni?: string;
    degree?: string;
    field?: string;
    tier?: string;
    start_year?: number;
    end_year?: number;
}

interface CandidateMatch {
    id: string;
    name: string;
    email: string;
    city?: string;
    location: string;
    current_role: string;
    current_company?: string;
    years_experience: number;
    education_summary: string;
    company_tier: string;
    skills: string[];
    match_score: number; // 0-100 weighted percentage (5-signal combined score)
    similarity_score: number; // 0-100 semantic similarity percentage
    // NEW v3.0: Multi-signal scoring components
    experience_relevance_score?: number; // 0-100 how well past job titles match the job
    skill_overlap_score?: number; // 0-100 how many required skills candidate has
    title_similarity_score?: number; // 0-100 current role vs job title match
    cv_url?: string | null;
    match_rank: number;
    // Additional profile fields for candidate detail modal
    summary?: string | null;
    linkedin_url?: string | null;
    has_startup_experience?: boolean;
    has_founded_company?: boolean;
    has_side_projects?: boolean;
    // Tier fields for filtering
    education_tier?: number; // 1-3 (1=Top tier, 3=Lower tier)
    experience_tier?: number; // 1-3 (1=Top tier, 3=Lower tier)
    // Education fields for enhanced display
    university?: string;
    degree?: string;
    educations?: EducationHistory[]; // All education entries for timeline
    // Company history for timeline
    companies?: CompanyHistory[];
}



// Skeleton components for loading states
const HeaderSkeleton = () => (
    <div className="mb-6 flex-shrink-0 animate-pulse">
        <div className="h-9 bg-slate-200 rounded-lg w-40 mb-3"></div>
        <div className="h-5 bg-slate-200 rounded w-96"></div>
    </div>
);

const FiltersSkeleton = () => (
    <div className="space-y-4 md:space-y-6 animate-pulse">
        {/* Job Selection Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 md:p-6">
            <div className="h-4 bg-slate-200 rounded w-24 mb-4"></div>
            <div className="h-12 bg-slate-100 rounded-xl w-full"></div>
        </div>
        {/* Filters Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 md:p-6">
            <div className="h-4 bg-slate-200 rounded w-32 mb-4"></div>
            <div className="space-y-4">
                <div className="h-10 bg-slate-100 rounded-lg w-full"></div>
                <div className="h-10 bg-slate-100 rounded-lg w-full"></div>
                <div className="h-10 bg-slate-100 rounded-lg w-full"></div>
            </div>
        </div>
    </div>
);

const CandidatesTableSkeleton = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden flex flex-col h-full">
        {/* Table Header - Hidden on mobile */}
        <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-100 flex-shrink-0 animate-pulse">
            <div className="col-span-1 h-4 bg-slate-200 rounded w-8 mx-auto"></div>
            <div className="col-span-3 h-4 bg-slate-200 rounded w-24"></div>
            <div className="col-span-3 h-4 bg-slate-200 rounded w-20"></div>
            <div className="col-span-1 h-4 bg-slate-200 rounded w-8 mx-auto"></div>
            <div className="col-span-2 h-4 bg-slate-200 rounded w-16"></div>
            <div className="col-span-2"></div>
        </div>

        {/* Table Body */}
        <div className="divide-y divide-slate-100 flex-1 overflow-y-auto">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse">
                    {/* Desktop Skeleton */}
                    <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-5 items-center">
                        <div className="col-span-1 flex justify-center">
                            <div className="h-6 w-6 bg-slate-200 rounded"></div>
                        </div>
                        <div className="col-span-3 flex items-center gap-3">
                            <div className="w-11 h-11 bg-slate-200 rounded-full flex-shrink-0"></div>
                            <div className="min-w-0 flex-1 space-y-2">
                                <div className="h-4 bg-slate-200 rounded w-28"></div>
                                <div className="h-3 bg-slate-100 rounded w-20"></div>
                            </div>
                        </div>
                        <div className="col-span-3 h-4 bg-slate-200 rounded w-24"></div>
                        <div className="col-span-1 flex justify-center">
                            <div className="h-4 bg-slate-200 rounded w-8"></div>
                        </div>
                        <div className="col-span-2 h-4 bg-slate-200 rounded w-20"></div>
                        <div className="col-span-2 flex justify-end">
                            <div className="w-24 h-9 bg-slate-100 rounded-lg"></div>
                        </div>
                    </div>

                    {/* Mobile Skeleton */}
                    <div className="lg:hidden p-4 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-slate-200 rounded-xl flex-shrink-0"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                                <div className="h-3 bg-slate-100 rounded w-1/2"></div>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <div className="h-6 bg-slate-100 rounded-lg w-20"></div>
                            <div className="h-6 bg-slate-100 rounded-lg w-24"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>

        {/* Table Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex-shrink-0 animate-pulse">
            <div className="h-4 bg-slate-200 rounded w-48 mx-auto md:mx-0"></div>
        </div>
    </div>
);

// Candidate Detail Modal Component
interface CandidateDetailModalProps {
    candidate: CandidateMatch | null;
    onClose: () => void;
    jobTitle?: string;
    isPurchased: boolean;
    onPurchase: (candidateId: string, candidateName: string) => void;
    censorName: (name: string) => string;
}

const CandidateDetailModal: React.FC<CandidateDetailModalProps> = ({
    candidate,
    onClose,
    jobTitle,
    isPurchased,
    onPurchase,
    censorName
}) => {
    if (!candidate) return null;

    const getMatchScoreGradient = (score: number) => {
        if (score >= 85) return 'from-emerald-500 to-teal-600';
        if (score >= 75) return 'from-amber-500 to-orange-600';
        if (score >= 65) return 'from-slate-500 to-slate-600';
        return 'from-slate-400 to-slate-500';
    };



    // Helper to format date range for timeline
    const formatDateRange = (startYear?: number, endYear?: number) => {
        const start = startYear ? startYear.toString() : '';
        const end = endYear ? endYear.toString() : 'Present';
        if (!start && !endYear) return ''; // No dates at all
        if (!start) return end; // Only end year
        return `${start} — ${end}`;
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-5xl max-h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-scale-in">
                <div className="relative flex-shrink-0">
                    <div className={`h-28 md:h-40 bg-gradient-to-r ${getMatchScoreGradient(candidate.match_score)}`} />

                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 md:top-6 md:right-6 p-2.5 bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-md group z-10"
                    >
                        <X className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
                    </button>

                    {/* Header Content Container */}
                    <div className="px-6 md:px-10 absolute -bottom-6 w-full flex justify-between items-end">
                        <div className="flex items-end gap-4 md:gap-6">
                            {/* Avatar */}
                            <div className="w-20 h-20 md:w-28 md:h-28 bg-white p-1 md:p-1.5 rounded-2xl shadow-xl flex-shrink-0">
                                <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl flex items-center justify-center text-white font-bold text-2xl md:text-3xl">
                                    {candidate.name.split(' ').map(n => n[0]).join('')}
                                </div>
                            </div>

                            {/* Name, Role, and Company - visible on desktop over gradient */}
                            <div className="mb-4 md:mb-8 hidden md:block text-white shadow-black/20 drop-shadow-md">
                                {isPurchased ? (
                                    <h2 className="text-2xl md:text-3xl font-bold">{candidate.name}</h2>
                                ) : (
                                    <button
                                        onClick={() => onPurchase(candidate.id, candidate.name)}
                                        className="text-2xl md:text-3xl font-bold hover:opacity-80 transition-opacity flex items-center gap-2 group"
                                    >
                                        {censorName(candidate.name)}
                                        <Lock className="w-5 h-5 text-yellow-300 group-hover:scale-110 transition-transform" />
                                    </button>
                                )}
                                <p className="text-base md:text-lg opacity-90 font-medium">{candidate.current_role}</p>
                                {candidate.current_company && (
                                    <p className="text-sm opacity-80 flex items-center gap-1.5 mt-0.5">
                                        <Building2 className="w-4 h-4" />
                                        {candidate.current_company}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Rank Badge */}
                        <div className="hidden md:flex items-center gap-3 bg-white px-5 py-3 rounded-xl shadow-lg border border-slate-100 mb-2">
                            <div className="p-2 rounded-full bg-slate-50">
                                <TrendingUp className="w-6 h-6 text-slate-700" />
                            </div>
                            <div>
                                <div className="text-2xl font-bold text-slate-900 leading-none">#{candidate.match_rank}</div>
                                <div className="text-xs font-semibold text-slate-500 mt-1 uppercase tracking-wide">Overall Rank</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto pt-10 px-6 md:px-10 pb-10">
                    {/* Mobile Header (Name/Role/Company/Badge) */}
                    <div className="md:hidden mb-6 space-y-3">
                        <div>
                            {isPurchased ? (
                                <h2 className="text-2xl font-bold text-slate-900">{candidate.name}</h2>
                            ) : (
                                <button
                                    onClick={() => onPurchase(candidate.id, candidate.name)}
                                    className="text-2xl font-bold text-slate-900 hover:text-blue-600 transition-colors flex items-center gap-2 group"
                                >
                                    {censorName(candidate.name)}
                                    <Lock className="w-5 h-5 text-blue-600 group-hover:scale-110 transition-transform" />
                                </button>
                            )}
                            <p className="text-lg text-slate-600">{candidate.current_role}</p>
                            {candidate.current_company && (
                                <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5">
                                    <Building2 className="w-4 h-4" />
                                    {candidate.current_company}
                                </p>
                            )}
                        </div>
                        <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-lg border border-slate-100 w-fit">
                            <TrendingUp className="w-5 h-5 text-slate-700" />
                            <span className="font-bold text-slate-900">Rank #{candidate.match_rank}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                        {/* Main Column */}
                        <div className="lg:col-span-8 space-y-6">
                            {/* Education Section - All Education Entries */}
                            {candidate.educations && candidate.educations.length > 0 ? (
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 p-5 rounded-2xl border border-slate-200">
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-100">
                                            <GraduationCap className="w-6 h-6 text-slate-700" />
                                        </div>
                                        <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Education</h3>
                                    </div>
                                    <div className="space-y-3">
                                        {candidate.educations.map((edu, idx) => (
                                            <div key={idx} className={`p-4 bg-white rounded-xl border border-slate-100 shadow-sm ${idx === 0 ? 'ring-1 ring-emerald-100' : ''}`}>
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1 min-w-0">
                                                        {edu.degree && (
                                                            <p className="font-semibold text-slate-900">
                                                                {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                                                            </p>
                                                        )}
                                                        {edu.uni && (
                                                            <p className="text-slate-600 text-sm mt-0.5">{edu.uni}</p>
                                                        )}
                                                    </div>
                                                    {formatDateRange(edu.start_year, edu.end_year) && (
                                                        <div className="text-xs text-slate-400 flex-shrink-0">
                                                            {formatDateRange(edu.start_year, edu.end_year)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (candidate.university || candidate.degree) && (
                                <div className="bg-gradient-to-br from-slate-50 to-slate-100/50 p-5 rounded-2xl border border-slate-200">
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 bg-white rounded-xl shadow-sm border border-slate-100">
                                            <GraduationCap className="w-6 h-6 text-slate-700" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Education</h3>
                                            {candidate.degree && (
                                                <p className="font-semibold text-slate-900 text-lg">{candidate.degree}</p>
                                            )}
                                            {candidate.university && (
                                                <p className="text-slate-600 mt-0.5">{candidate.university}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Unified Contact Section - Email, CV, LinkedIn */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {/* Email */}
                                {isPurchased ? (
                                    <button
                                        onClick={() => navigator.clipboard.writeText(candidate.email)}
                                        className="flex flex-col items-center justify-center p-4 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md rounded-xl transition-all group text-center"
                                    >
                                        <div className="p-2.5 bg-slate-100 text-slate-600 rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                            <Mail className="w-5 h-5" />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</span>
                                        <span className="text-sm font-medium text-slate-700 truncate max-w-full mt-0.5">{candidate.email}</span>
                                        <span className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                            <Copy className="w-3 h-3" /> Click to copy
                                        </span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => onPurchase(candidate.id, candidate.name)}
                                        className="flex flex-col items-center justify-center p-4 bg-white border-2 border-blue-200 hover:border-blue-400 rounded-xl transition-all group text-center"
                                    >
                                        <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                            <Lock className="w-5 h-5" />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</span>
                                        <span className="text-sm font-medium text-slate-400 mt-0.5" style={{ filter: 'blur(4px)' }}>email@hidden.com</span>
                                        <span className="text-[10px] text-blue-600 font-semibold mt-1 flex items-center gap-1">
                                            <Unlock className="w-3 h-3" /> Unlock
                                        </span>
                                    </button>
                                )}

                                {/* CV */}
                                {isPurchased ? (
                                    candidate.cv_url ? (
                                        <a
                                            href={candidate.cv_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex flex-col items-center justify-center p-4 bg-white border border-slate-200 hover:border-red-200 hover:shadow-md rounded-xl transition-all group text-center"
                                        >
                                            <div className="p-2.5 bg-red-50 text-red-600 rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                                <Download className="w-5 h-5" />
                                            </div>
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CV / Resume</span>
                                            <span className="text-sm font-medium text-slate-700 mt-0.5">Download PDF</span>
                                            <span className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                                <ExternalLink className="w-3 h-3" /> Opens in new tab
                                            </span>
                                        </a>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl text-center opacity-60">
                                            <div className="p-2.5 bg-slate-100 text-slate-400 rounded-lg mb-2">
                                                <FileText className="w-5 h-5" />
                                            </div>
                                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">CV / Resume</span>
                                            <span className="text-sm font-medium text-slate-400 mt-0.5">Not available</span>
                                        </div>
                                    )
                                ) : (
                                    <button
                                        onClick={() => onPurchase(candidate.id, candidate.name)}
                                        className="flex flex-col items-center justify-center p-4 bg-white border-2 border-blue-200 hover:border-blue-400 rounded-xl transition-all group text-center"
                                    >
                                        <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                            <Lock className="w-5 h-5" />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CV / Resume</span>
                                        <span className="text-sm font-medium text-slate-400 mt-0.5">Download PDF</span>
                                        <span className="text-[10px] text-blue-600 font-semibold mt-1 flex items-center gap-1">
                                            <Unlock className="w-3 h-3" /> Unlock
                                        </span>
                                    </button>
                                )}

                                {/* LinkedIn */}
                                {isPurchased ? (
                                    candidate.linkedin_url ? (
                                        <a
                                            href={candidate.linkedin_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex flex-col items-center justify-center p-4 bg-white border border-slate-200 hover:border-[#0A66C2]/30 hover:shadow-md rounded-xl transition-all group text-center"
                                        >
                                            <div className="p-2.5 bg-[#0A66C2]/10 text-[#0A66C2] rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                                <Linkedin className="w-5 h-5" />
                                            </div>
                                            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">LinkedIn</span>
                                            <span className="text-sm font-medium text-slate-700 mt-0.5">View Profile</span>
                                            <span className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
                                                <ExternalLink className="w-3 h-3" /> Opens in new tab
                                            </span>
                                        </a>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl text-center opacity-60">
                                            <div className="p-2.5 bg-slate-100 text-slate-400 rounded-lg mb-2">
                                                <Linkedin className="w-5 h-5" />
                                            </div>
                                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">LinkedIn</span>
                                            <span className="text-sm font-medium text-slate-400 mt-0.5">Not available</span>
                                        </div>
                                    )
                                ) : (
                                    <button
                                        onClick={() => onPurchase(candidate.id, candidate.name)}
                                        className="flex flex-col items-center justify-center p-4 bg-white border-2 border-blue-200 hover:border-blue-400 rounded-xl transition-all group text-center"
                                    >
                                        <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg mb-2 group-hover:scale-110 transition-transform">
                                            <Lock className="w-5 h-5" />
                                        </div>
                                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">LinkedIn</span>
                                        <span className="text-sm font-medium text-slate-400 mt-0.5">View Profile</span>
                                        <span className="text-[10px] text-blue-600 font-semibold mt-1 flex items-center gap-1">
                                            <Unlock className="w-3 h-3" /> Unlock
                                        </span>
                                    </button>
                                )}
                            </div>

                            {/* Key Details Row */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 bg-slate-50 rounded-2xl border border-slate-100">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <MapPin className="w-5 h-5 text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Location</p>
                                        <p className="font-semibold text-slate-900">
                                            {candidate.city ? `${candidate.city}, ` : ''}{candidate.location}
                                        </p>
                                    </div>
                                </div>
                                <div className="w-px h-10 bg-slate-200 hidden sm:block"></div>
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <Briefcase className="w-5 h-5 text-slate-500" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Experience</p>
                                        <p className="font-semibold text-slate-900">{candidate.years_experience} Years</p>
                                    </div>
                                </div>
                            </div>

                            {/* Summary */}
                            {candidate.summary && (
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-slate-400" />
                                        Professional Summary
                                    </h3>
                                    <div className="text-slate-700 leading-relaxed bg-white p-4 rounded-xl border border-slate-100">
                                        {candidate.summary}
                                    </div>
                                </div>
                            )}

                            {/* Background Tags */}
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                                    <Award className="w-5 h-5 text-slate-400" />
                                    Background & Achievements
                                </h3>
                                <div className="flex flex-wrap gap-2 sm:gap-3">
                                    <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors border border-slate-200">
                                        <Building2 className="w-4 h-4 text-slate-600" />
                                        <span className="font-medium text-slate-700 text-sm sm:text-base">{candidate.company_tier} Companies</span>
                                    </div>
                                    {candidate.has_startup_experience && (
                                        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-orange-50 hover:bg-orange-100 rounded-xl transition-colors border border-orange-100">
                                            <Rocket className="w-4 h-4 text-orange-600" />
                                            <span className="font-medium text-orange-800 text-sm sm:text-base">Startup Experience</span>
                                        </div>
                                    )}
                                    {candidate.has_founded_company && (
                                        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors border border-emerald-100">
                                            <Star className="w-4 h-4 text-emerald-600" />
                                            <span className="font-medium text-emerald-800 text-sm sm:text-base">Founded a Company</span>
                                        </div>
                                    )}
                                    {candidate.has_side_projects && (
                                        <div className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors border border-blue-100">
                                            <Lightbulb className="w-4 h-4 text-blue-600" />
                                            <span className="font-medium text-blue-800 text-sm sm:text-base">Side Projects</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Skills */}
                            {candidate.skills && candidate.skills.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                                        <Sparkles className="w-5 h-5 text-slate-400" />
                                        Skills & Expertise
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {candidate.skills.map((skill, idx) => (
                                            <span
                                                key={idx}
                                                className="px-3 py-1.5 text-sm font-medium bg-white text-slate-700 rounded-lg border border-slate-200 shadow-sm"
                                            >
                                                {skill}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Sidebar Column - Career Timeline */}
                        <div className="lg:col-span-4 space-y-6">
                            {/* Career Timeline */}
                            {candidate.companies && candidate.companies.length > 0 && (
                                <div className="bg-slate-50 rounded-2xl p-5 sm:p-6 border border-slate-100 flex flex-col max-h-[500px]">
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-5 flex items-center gap-2 flex-shrink-0">
                                        <Building2 className="w-4 h-4 text-slate-500" />
                                        Career Timeline
                                    </h3>
                                    <div className="relative overflow-y-auto pr-2 custom-scrollbar">
                                        {/* Timeline line */}
                                        <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-slate-200"></div>

                                        <div className="space-y-4">
                                            {candidate.companies.map((company, idx) => (
                                                <div key={idx} className="relative flex gap-4">
                                                    {/* Timeline dot */}
                                                    <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${idx === 0
                                                        ? 'bg-emerald-500 ring-4 ring-emerald-100'
                                                        : 'bg-white border-2 border-slate-300'
                                                        }`}>
                                                        {idx === 0 && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                                    </div>

                                                    {/* Content */}
                                                    <div className="flex-1 pb-4">
                                                        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
                                                            <p className="font-semibold text-slate-900 text-sm">{company.name}</p>
                                                            <p className="text-slate-600 text-sm mt-0.5">{company.role}</p>
                                                            {formatDateRange(company.start_year, company.end_year) && (
                                                                <p className="text-xs text-slate-400 mt-2">
                                                                    {formatDateRange(company.start_year, company.end_year)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Matched For Role - only shown if no timeline or as secondary info */}
                            {jobTitle && (
                                <div className="bg-slate-50 rounded-2xl p-5 sm:p-6 border border-slate-100">
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3">
                                        Matched For
                                    </h3>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-white rounded-lg shadow-sm border border-slate-100">
                                            <Briefcase className="w-5 h-5 text-slate-600" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-slate-900">{jobTitle}</p>
                                            <p className="text-xs text-slate-500 mt-1">This candidate was matched for this role</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="flex-shrink-0 px-6 py-4 md:px-10 md:py-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-end gap-3 sm:gap-4">
                    <button
                        onClick={onClose}
                        className="w-full sm:w-auto px-6 py-3 font-semibold text-slate-600 hover:text-slate-900 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl transition-colors order-2 sm:order-1"
                    >
                        Close Details
                    </button>
                    {isPurchased ? (
                        <a
                            href={`mailto:${candidate.email}`}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 order-1 sm:order-2"
                        >
                            <Mail className="w-5 h-5" />
                            Contact Candidate
                        </a>
                    ) : (
                        <button
                            onClick={() => onPurchase(candidate.id, candidate.name)}
                            className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 order-1 sm:order-2"
                        >
                            <Lock className="w-5 h-5" />
                            Unlock to Contact
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export const HirerCandidatesPage: React.FC = () => {
    // PAYMENT_DISABLED: Set to false to disable all payment functionality
    // Payment code is preserved but bypassed via this flag for future reuse
    const PAYMENT_ENABLED = false;

    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [jobs, setJobs] = useState<HiringJob[]>([]);
    const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<CandidateMatch[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [searchQuery, setSearchQuery] = useState('');
    const [isJobDropdownOpen, setIsJobDropdownOpen] = useState(false);
    const [matchError, setMatchError] = useState<string | null>(null);
    const [selectedCandidate, setSelectedCandidate] = useState<CandidateMatch | null>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

    // BULK UPLOAD STATE
    const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
    const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'completed' | 'error'>('idle');
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
    const [processingProgress, setProcessingProgress] = useState<{ processed: number; total: number; failed: number }>({ processed: 0, total: 0, failed: 0 });
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [isExtractingZip, setIsExtractingZip] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    // Purchase state (PAYMENT_DISABLED: kept for future reuse)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [purchasedCandidateIds, setPurchasedCandidateIds] = useState<string[]>([]);
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [top25Data, setTop25Data] = useState<{ count: number; candidateIds: string[] } | null>(null);
    const [isProcessingPurchase, setIsProcessingPurchase] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [purchaseRefetchTrigger, setPurchaseRefetchTrigger] = useState(0);

    // Delete candidate state
    const [deletingCandidateId, setDeletingCandidateId] = useState<string | null>(null);
    const [showClearAllModal, setShowClearAllModal] = useState(false);
    const [isClearingAll, setIsClearingAll] = useState(false);

    // Check for payment success
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const paymentSuccess = searchParams.get('payment') === 'success';

    // Helper: Check if candidate is purchased (PAYMENT_DISABLED: always return true)
    const isCandidatePurchased = (candidateId: string) => PAYMENT_ENABLED ? purchasedCandidateIds.includes(candidateId) : true;

    // Helper: Censor name (replace ~60% with asterisks)
    const censorName = (name: string) => {
        if (!name) return '***';
        const chars = name.split('');
        const censorCount = Math.ceil(chars.length * 0.6);
        const startIndex = Math.floor((chars.length - censorCount) / 2);
        for (let i = startIndex; i < startIndex + censorCount; i++) {
            if (chars[i] !== ' ') chars[i] = '*';
        }
        return chars.join('');
    };

    // Trigger refetch when returning from successful payment
    /* DISABLED - PAYMENT_ENABLED = false
    useEffect(() => {
        if (paymentSuccess) {
            console.log('[HirerCandidates] Payment success detected - triggering purchase refetch');
            setPurchaseRefetchTrigger(prev => prev + 1);
        }
    }, [paymentSuccess]);
    */

    // Fetch purchased candidates on mount and when trigger changes - PAYMENT_DISABLED: Not needed when payments disabled
    /* DISABLED - PAYMENT_ENABLED = false
    useEffect(() => {
        if (!user) return;
        const fetchPurchasedIds = async () => {
            try {
                const candidateIds = candidates.map(c => c.id);
                if (candidateIds.length === 0) return;

                console.log('[HirerCandidates] Checking purchases for', candidateIds.length, 'candidates');
                console.log('[HirerCandidates] Sample candidate IDs:', candidateIds.slice(0, 3));

                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/check-purchases`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userData: { sub: user.id }, candidateUserIds: candidateIds })
                });
                const data = await response.json();
                console.log('[HirerCandidates] Purchase check response:', data);
                if (data.success) {
                    console.log('[HirerCandidates] Purchased IDs:', data.purchasedIds);
                    setPurchasedCandidateIds(data.purchasedIds || []);
                }
            } catch (error) {
                console.error('Error fetching purchase status:', error);
            }
        };
        fetchPurchasedIds();
    }, [user, candidates, purchaseRefetchTrigger]);
    */

    // Fetch top 25% candidates count - PAYMENT_DISABLED: Not needed when payments disabled
    /* DISABLED - PAYMENT_ENABLED = false
    useEffect(() => {
        if (!selectedJobId || !user) return;
        const fetchTop25 = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/top-candidates-count`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userData: { sub: user.id }, jobId: selectedJobId })
                });
                const data = await response.json();
                if (data.success) {
                    setTop25Data({ count: data.top25Count, candidateIds: data.top25CandidateIds });
                }
            } catch (error) {
                console.error('Error fetching top 25% data:', error);
            }
        };
        fetchTop25();
    }, [selectedJobId, user]);
    */

    // Handle purchase
    const handlePurchase = async (purchaseType: 'BULK' | 'SINGLE', candidateIds: string[], candidateName?: string) => {
        if (!user || !selectedJobId) return;
        setIsProcessingPurchase(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/create-checkout-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: { sub: user.id },
                    jobId: selectedJobId,
                    purchaseType,
                    candidateUserIds: candidateIds,
                    candidateName
                })
            });

            const data = await response.json();
            if (data.success && data.url) {
                window.location.href = data.url;
            } else {
                alert('Failed to create checkout session. Please try again.');
            }
        } catch (error) {
            console.error('Purchase error:', error);
            alert('Something went wrong. Please try again.');
        } finally {
            setIsProcessingPurchase(false);
        }
    };

    // Filter states
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [maxEducationTier, setMaxEducationTier] = useState<number | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [maxExperienceTier, setMaxExperienceTier] = useState<number | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [minYearsExperience, setMinYearsExperience] = useState<string>('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 25;

    // Reset pagination when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, maxEducationTier, maxExperienceTier, minYearsExperience, selectedJobId]);

    // Scroll to top when page changes
    useEffect(() => {
        if (tableContainerRef.current) {
            tableContainerRef.current.scrollTop = 0;
        }
    }, [currentPage]);

    // Fetch hirer's jobs
    useEffect(() => {
        if (!user) return;

        const fetchJobs = async () => {
            try {
                setLoading(true);

                // Get hirer record
                const { data: hirerData } = await supabase
                    .from('hirers')
                    .select('id')
                    .eq('user_id', user.id)
                    .single();

                if (hirerData) {
                    // Fetch jobs
                    const { data: jobsData } = await supabase
                        .from('hiring_jobs')
                        .select('*')
                        .eq('hirer_id', hirerData.id)
                        .order('created_at', { ascending: false });

                    if (jobsData && jobsData.length > 0) {
                        setJobs(jobsData);
                        setSelectedJobId(jobsData[0].id); // Select first job by default
                    }
                }
            } catch (error) {
                console.error('Error fetching jobs:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchJobs();
    }, [user]);

    // BULK UPLOAD HANDLERS
    const VALID_CV_EXTENSIONS = ['pdf', 'docx', 'doc', 'txt'];

    const isValidCVFile = (filename: string): boolean => {
        const ext = filename.toLowerCase().split('.').pop();
        return VALID_CV_EXTENSIONS.includes(ext || '');
    };

    const getMimeType = (filename: string): string => {
        const ext = filename.toLowerCase().split('.').pop();
        switch (ext) {
            case 'pdf': return 'application/pdf';
            case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            case 'doc': return 'application/msword';
            case 'txt': return 'text/plain';
            default: return 'application/octet-stream';
        }
    };

    const extractFilesFromZip = async (zipFile: File): Promise<File[]> => {
        setIsExtractingZip(true);
        try {
            const zip = await JSZip.loadAsync(zipFile);
            const extractedFiles: File[] = [];

            const entries = Object.entries(zip.files);
            for (const [relativePath, zipEntry] of entries) {
                // Skip directories and hidden files (like __MACOSX)
                if (zipEntry.dir || relativePath.startsWith('__MACOSX') || relativePath.startsWith('.')) {
                    continue;
                }

                const filename = relativePath.split('/').pop() || '';
                if (isValidCVFile(filename)) {
                    const blob = await zipEntry.async('blob');
                    const mimeType = getMimeType(filename);
                    const file = new File([blob], filename, { type: mimeType });
                    extractedFiles.push(file);
                }
            }

            return extractedFiles;
        } finally {
            setIsExtractingZip(false);
        }
    };

    const isZipFile = (filename: string): boolean => {
        return filename.toLowerCase().endsWith('.zip');
    };

    const traverseFileTree = (entry: FileSystemEntry): Promise<File[]> => {
        return new Promise((resolve) => {
            if (entry.isFile) {
                (entry as FileSystemFileEntry).file((file) => {
                    // Accept both CV files and ZIP files (ZIPs will be extracted later)
                    if (isValidCVFile(file.name) || isZipFile(file.name)) {
                        resolve([file]);
                    } else {
                        resolve([]);
                    }
                }, () => resolve([]));
            } else if (entry.isDirectory) {
                const dirReader = (entry as FileSystemDirectoryEntry).createReader();
                const allFiles: File[] = [];

                const readEntries = () => {
                    dirReader.readEntries(async (entries) => {
                        if (entries.length === 0) {
                            resolve(allFiles);
                        } else {
                            for (const childEntry of entries) {
                                const files = await traverseFileTree(childEntry);
                                allFiles.push(...files);
                            }
                            readEntries(); // Continue reading (readEntries may return batches)
                        }
                    }, () => resolve(allFiles));
                };
                readEntries();
            } else {
                resolve([]);
            }
        });
    };

    const handleFileSelect = async (files: FileList | File[]) => {
        const fileArray = Array.from(files);
        const cvFiles: File[] = [];
        const zipFiles: File[] = [];

        // Separate ZIP files from CV files
        for (const file of fileArray) {
            const ext = file.name.toLowerCase().split('.').pop();
            if (ext === 'zip') {
                zipFiles.push(file);
            } else if (isValidCVFile(file.name)) {
                cvFiles.push(file);
            }
        }

        // Extract files from ZIP archives
        for (const zipFile of zipFiles) {
            try {
                const extractedFiles = await extractFilesFromZip(zipFile);
                cvFiles.push(...extractedFiles);
            } catch (error) {
                console.error(`Failed to extract ${zipFile.name}:`, error);
                setMatchError(`Failed to extract ZIP file: ${zipFile.name}`);
            }
        }

        if (cvFiles.length === 0) {
            setMatchError('Please select valid CV files (PDF, DOCX, DOC, TXT) or a ZIP/folder containing them');
            return;
        }

        setUploadingFiles(cvFiles);
        setUploadStatus('idle');
        setMatchError(null);
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(false);

        const items = e.dataTransfer.items;
        const allFiles: File[] = [];

        // Use webkitGetAsEntry for folder support
        if (items && items.length > 0) {
            const entries: FileSystemEntry[] = [];
            for (let i = 0; i < items.length; i++) {
                const entry = items[i].webkitGetAsEntry?.();
                if (entry) {
                    entries.push(entry);
                }
            }

            if (entries.length > 0) {
                setIsExtractingZip(true);
                try {
                    for (const entry of entries) {
                        const files = await traverseFileTree(entry);
                        allFiles.push(...files);
                    }
                } finally {
                    setIsExtractingZip(false);
                }

                if (allFiles.length > 0) {
                    await handleFileSelect(allFiles);
                    return;
                }
            }
        }

        // Fallback to regular file handling (also handles ZIP files)
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            await handleFileSelect(e.dataTransfer.files);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDraggingOver(false);
    };

    const startBulkUpload = async () => {
        if (!selectedJobId || !user || uploadingFiles.length === 0) return;

        setUploadStatus('uploading');
        setMatchError(null);

        try {
            // Step 1: Start the batch
            const startResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/bulk-upload/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: { sub: user.id },
                    jobId: selectedJobId,
                    files: uploadingFiles.map(f => ({
                        filename: f.name,
                        size: f.size,
                        contentType: f.type
                    }))
                })
            });

            const startData = await startResponse.json();
            if (!startData.success) {
                throw new Error(startData.error || 'Failed to start upload');
            }

            setCurrentBatchId(startData.batchId);
            const { batchId, hirerId, jobId: batchJobId } = startData;

            // Step 2: Upload files in parallel batches for faster uploads
            const UPLOAD_CONCURRENCY = 5; // Upload 5 files at a time
            const uploadFile = async (file: File) => {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('batchId', batchId);
                formData.append('hirerId', hirerId);
                formData.append('jobId', batchJobId);

                const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/bulk-upload/upload-file`, {
                    method: 'POST',
                    body: formData
                });

                const uploadData = await uploadResponse.json();
                if (!uploadData.success) {
                    console.error(`Failed to upload ${file.name}:`, uploadData.error);
                }

                setUploadProgress(prev => ({
                    ...prev,
                    [file.name]: 100
                }));

                return uploadData;
            };

            // Process uploads in parallel batches
            for (let i = 0; i < uploadingFiles.length; i += UPLOAD_CONCURRENCY) {
                const batch = uploadingFiles.slice(i, i + UPLOAD_CONCURRENCY);
                await Promise.all(batch.map(uploadFile));
            }

            // Step 3: Trigger processing
            setUploadStatus('processing');
            setProcessingProgress({ processed: 0, total: uploadingFiles.length, failed: 0 });

            const processResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/bulk-upload/process`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userData: { sub: user.id },
                    batchId
                })
            });

            const processData = await processResponse.json();
            if (!processData.success) {
                throw new Error(processData.error || 'Failed to start processing');
            }

            // Step 4: Poll for status (faster polling for better UX with parallel processing)
            const pollStatus = async () => {
                const statusResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/bulk-upload/status/${batchId}`);
                const statusData = await statusResponse.json();

                if (statusData.success) {
                    setProcessingProgress({
                        processed: statusData.batch.processedCount,
                        total: statusData.batch.totalFiles,
                        failed: statusData.batch.failedCount
                    });

                    if (statusData.batch.status === 'completed') {
                        setUploadStatus('completed');
                        setUploadingFiles([]);
                        setUploadProgress({});
                        // Refresh candidates
                        fetchUploadedCandidates();
                    } else if (statusData.batch.status === 'failed') {
                        throw new Error('Batch processing failed');
                    } else {
                        // Continue polling - faster interval since parallel processing updates more frequently
                        setTimeout(pollStatus, 1000);
                    }
                }
            };

            // Start polling quickly since parallel processing starts immediately
            setTimeout(pollStatus, 1500);

        } catch (error) {
            console.error('Bulk upload error:', error);
            setUploadStatus('error');
            setMatchError(error instanceof Error ? error.message : 'Upload failed');
        }
    };

    const fetchUploadedCandidates = useCallback(async () => {
        if (!selectedJobId || !user) {
            setCandidates([]);
            return;
        }

        setLoadingCandidates(true);
        setMatchError(null);

        try {
            // Use the new uploaded-candidates endpoint instead of job-matches
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/uploaded-candidates/${selectedJobId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userData: { sub: user.id }
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to fetch matches');
            }

            const data = await response.json();

            if (data.success) {
                setCandidates(data.candidates || []);
            } else {
                throw new Error(data.message || 'Failed to fetch matches');
            }
        } catch (error) {
            console.error('Error fetching candidates:', error);
            // Don't show error if just no candidates yet - that's expected for new uploads
            if (error instanceof Error && !error.message.includes('not found')) {
                setMatchError(error.message);
            }
            setCandidates([]);
        } finally {
            setLoadingCandidates(false);
        }
    }, [selectedJobId, user]);

    // Fetch uploaded candidates when job selection changes
    useEffect(() => {
        if (!selectedJobId || !user) {
            setCandidates([]);
            return;
        }

        fetchUploadedCandidates();
    }, [selectedJobId, user, fetchUploadedCandidates]);

    // Delete a single candidate
    const handleDeleteCandidate = async (candidateId: string) => {
        if (!user) return;

        setDeletingCandidateId(candidateId);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/uploaded-candidate/${candidateId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userData: { sub: user.id } })
            });

            const data = await response.json();
            if (data.success) {
                // Remove candidate from local state
                setCandidates(prev => prev.filter(c => c.id !== candidateId));
            } else {
                console.error('Failed to delete candidate:', data.error);
                setMatchError('Failed to delete candidate');
            }
        } catch (error) {
            console.error('Error deleting candidate:', error);
            setMatchError('Failed to delete candidate');
        } finally {
            setDeletingCandidateId(null);
        }
    };

    // Clear all candidates for the selected job
    const handleClearAllCandidates = async () => {
        if (!user || !selectedJobId) return;

        setIsClearingAll(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/hirer/uploaded-candidates/job/${selectedJobId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ userData: { sub: user.id } })
            });

            const data = await response.json();
            if (data.success) {
                // Clear candidates from local state
                setCandidates([]);
                setShowClearAllModal(false);
            } else {
                console.error('Failed to clear candidates:', data.error);
                setMatchError('Failed to clear candidates');
            }
        } catch (error) {
            console.error('Error clearing candidates:', error);
            setMatchError('Failed to clear candidates');
        } finally {
            setIsClearingAll(false);
        }
    };

    const selectedJob = jobs.find(job => job.id === selectedJobId);

    // Filter candidates by search query and filter criteria
    const filteredCandidates = candidates.filter(candidate => {
        // Search query filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch = (
                candidate.name.toLowerCase().includes(query) ||
                candidate.current_role.toLowerCase().includes(query) ||
                candidate.location.toLowerCase().includes(query) ||
                candidate.skills.some(skill => skill.toLowerCase().includes(query))
            );
            if (!matchesSearch) return false;
        }

        // Education tier filter
        // Tier 1 = show only tier 1 candidates
        // Tier 2 = show tier 1 and tier 2 candidates  
        // Tier 3 = show tier 1, 2, and 3 candidates
        if (maxEducationTier !== null) {
            const candidateEducationTier = candidate.education_tier;
            // Only apply filter if candidate HAS tier data
            // If they don't have tier data, let them pass through (permissive filtering)
            if (candidateEducationTier !== null && candidateEducationTier !== undefined) {
                // Exclude candidates whose tier is higher (worse) than the selected max
                if (candidateEducationTier > maxEducationTier) {
                    return false;
                }
            }
        }

        // Experience tier filter
        // Same logic: Tier 1 = show only tier 1, Tier 2 = show tier 1-2, etc.
        if (maxExperienceTier !== null) {
            const candidateExperienceTier = candidate.experience_tier;
            // Only apply filter if candidate HAS tier data
            // If they don't have tier data, let them pass through (permissive filtering)
            if (candidateExperienceTier !== null && candidateExperienceTier !== undefined) {
                // Exclude candidates whose tier is higher (worse) than the selected max
                if (candidateExperienceTier > maxExperienceTier) {
                    return false;
                }
            }
        }

        // Years of experience filter (candidate must have >= min years)
        if (minYearsExperience !== '') {
            const minYears = parseInt(minYearsExperience);
            if (!isNaN(minYears) && candidate.years_experience < minYears) {
                return false;
            }
        }

        return true;
    });

    // Pagination Logic
    const totalPages = Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginatedCandidates = filteredCandidates.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return (
        <div className="dashboard-layout">
            <CompanySidebar />

            <div className="main-content">
                <div className="flex flex-col h-full w-full min-h-0">
                    {/* Header */}
                    {loading ? (
                        <HeaderSkeleton />
                    ) : (
                        <div className="mb-6 flex-shrink-0 animate-fade-in">
                            <h1 className="heading-1 mb-2">Candidate Matches</h1>
                            <p className="text-body text-sm md:text-base">
                                View top-matched candidates for your job postings based on skills, experience, and fit.
                            </p>
                        </div>
                    )}

                    {/* Mobile Filter Toggle */}
                    <div className="lg:hidden mb-4 flex gap-2">
                        <button
                            onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-xl font-semibold text-slate-700 shadow-sm"
                        >
                            <Filter className="w-4 h-4" />
                            {isMobileFiltersOpen ? 'Hide Filters' : 'Show Filters'}
                        </button>
                    </div>

                    {/* Mobile Unlock Top 25% CTA - PAYMENT_DISABLED: hidden when payments disabled */}
                    {PAYMENT_ENABLED && selectedJobId && top25Data && top25Data.count > 0 && (
                        <div className="lg:hidden mb-4 bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 rounded-xl p-4 text-white shadow-lg animate-fade-in relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                            <div className="relative flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 bg-white/20 rounded-lg flex-shrink-0">
                                        <Flame className="w-5 h-5 text-yellow-300" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-bold truncate">Unlock Top 25%</h3>
                                        <p className="text-xs text-blue-100 truncate">
                                            {top25Data.count} top-ranked candidates
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowUnlockModal(true)}
                                    disabled={isProcessingPurchase}
                                    className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-white text-blue-700 font-bold text-sm rounded-lg hover:bg-blue-50 transition-colors shadow-md disabled:opacity-50"
                                >
                                    {isProcessingPurchase ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Unlock className="w-4 h-4" />
                                    )}
                                    Unlock
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Error Banner */}
                    {matchError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 animate-fade-in flex-shrink-0">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-semibold text-red-900">Error loading candidates</h3>
                                <p className="text-sm text-red-700 mt-1">{matchError}</p>
                            </div>
                        </div>
                    )}

                    {/* Two-Column Layout */}
                    <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-6 overflow-visible lg:overflow-hidden">
                        {/* Left Column: Job Selection & Filters */}
                        <div className={`lg:w-96 flex-shrink-0 flex flex-col gap-6 lg:overflow-y-auto lg:max-h-full ${isMobileFiltersOpen ? 'flex' : 'hidden lg:flex'}`}>
                            {loading ? (
                                <FiltersSkeleton />
                            ) : (
                                <>
                                    {/* Job Selection Card */}
                                    <div className={`bg-white rounded-2xl shadow-sm border border-slate-100 p-6 flex-shrink-0 animate-fade-in ${isJobDropdownOpen ? 'relative z-50' : ''}`}>
                                        <div className="flex items-center gap-2 mb-4">
                                            <Briefcase className="w-4 h-4 text-slate-600" />
                                            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                                                Job Posting
                                            </h3>
                                        </div>

                                        <div className="relative z-50">
                                            <button
                                                onClick={() => setIsJobDropdownOpen(!isJobDropdownOpen)}
                                                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-xl text-left hover:border-slate-300 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-200"
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0">
                                                        <Briefcase className="w-4 h-4 text-slate-700" />
                                                    </div>
                                                    <span className="font-medium text-slate-900 truncate">
                                                        {selectedJob?.job_title || 'Select a job posting'}
                                                    </span>
                                                </div>
                                                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform flex-shrink-0 ml-2 ${isJobDropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {isJobDropdownOpen && (
                                                <div className="absolute z-50 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                                                    {jobs.length === 0 ? (
                                                        <div className="p-4 text-center text-slate-500 text-sm">
                                                            No job postings found. Create one first.
                                                        </div>
                                                    ) : (
                                                        jobs.map(job => (
                                                            <button
                                                                key={job.id}
                                                                onClick={() => {
                                                                    setSelectedJobId(job.id);
                                                                    setIsJobDropdownOpen(false);
                                                                }}
                                                                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors ${selectedJobId === job.id ? 'bg-slate-50' : ''
                                                                    }`}
                                                            >
                                                                <div className={`p-2 rounded-lg flex-shrink-0 ${selectedJobId === job.id ? 'bg-slate-200' : 'bg-slate-100'}`}>
                                                                    <Briefcase className="w-4 h-4 text-slate-700" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="font-medium text-slate-900 truncate">{job.job_title}</p>
                                                                    <p className="text-sm text-slate-500">
                                                                        Posted {new Date(job.created_at).toLocaleDateString()}
                                                                    </p>
                                                                </div>
                                                                {selectedJobId === job.id && (
                                                                    <CheckCircle className="w-5 h-5 text-slate-900 flex-shrink-0" />
                                                                )}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Selected Job Summary */}
                                        {selectedJob && (
                                            <div className="mt-4 pt-4 border-t border-slate-100">
                                                <p className="text-xs text-slate-500 mb-1">Currently Viewing</p>
                                                <p className="font-medium text-slate-900">{selectedJob.job_title}</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* CV Upload Zone - New Bulk Upload Feature */}
                                    {selectedJobId && (
                                        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 flex-shrink-0 animate-fade-in">
                                            <div className="flex items-center gap-2 mb-4">
                                                <CloudUpload className="w-4 h-4 text-slate-600" />
                                                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                                                    Bulk Upload Candidate CVs
                                                </h3>
                                            </div>

                                            {/* Drag and Drop Zone */}
                                            <div
                                                onDrop={handleDrop}
                                                onDragOver={handleDragOver}
                                                onDragLeave={handleDragLeave}
                                                className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${isDraggingOver
                                                    ? 'border-blue-500 bg-blue-50'
                                                    : 'border-slate-200 bg-slate-50'
                                                    }`}
                                            >
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept=".pdf,.docx,.doc,.txt,.zip"
                                                    multiple
                                                    onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
                                                    className="hidden"
                                                />
                                                <input
                                                    ref={folderInputRef}
                                                    type="file"
                                                    // @ts-expect-error webkitdirectory is not in standard types
                                                    webkitdirectory=""
                                                    multiple
                                                    onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
                                                    className="hidden"
                                                />

                                                {isExtractingZip ? (
                                                    <>
                                                        <Loader2 className="w-8 h-8 mx-auto mb-2 text-blue-500 animate-spin" />
                                                        <p className="text-sm font-medium text-slate-700">
                                                            Extracting files...
                                                        </p>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Upload className={`w-8 h-8 mx-auto mb-2 ${isDraggingOver ? 'text-blue-500' : 'text-slate-400'}`} />
                                                        <p className="text-sm font-medium text-slate-700">
                                                            Drag & drop CVs, folders, or ZIP files
                                                        </p>
                                                        <p className="text-xs text-slate-500 mt-1 mb-3">
                                                            Supports PDF, DOCX, DOC, TXT
                                                        </p>

                                                        {/* Selection buttons */}
                                                        <div className="flex gap-2 justify-center">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    fileInputRef.current?.click();
                                                                }}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors"
                                                            >
                                                                <FileText className="w-3.5 h-3.5" />
                                                                Files
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    folderInputRef.current?.click();
                                                                }}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors"
                                                            >
                                                                <FolderOpen className="w-3.5 h-3.5" />
                                                                Folder
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            {/* Selected Files List */}
                                            {uploadingFiles.length > 0 && (
                                                <div className="mt-4 space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                                            {uploadingFiles.length} file(s) selected
                                                        </p>
                                                        {uploadStatus === 'idle' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setUploadingFiles([]);
                                                                    setUploadProgress({});
                                                                }}
                                                                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600 transition-colors"
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                                Clear all
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="max-h-32 overflow-y-auto space-y-1">
                                                        {uploadingFiles.map((file, idx) => (
                                                            <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-slate-50 rounded-lg">
                                                                <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                                                                <span className="flex-1 truncate text-slate-700">{file.name}</span>
                                                                {uploadProgress[file.name] === 100 ? (
                                                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                                ) : uploadStatus === 'uploading' ? (
                                                                    <Loader2 className="w-4 h-4 text-blue-500 animate-spin flex-shrink-0" />
                                                                ) : null}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Upload Button */}
                                            {uploadingFiles.length > 0 && uploadStatus === 'idle' && (
                                                <button
                                                    onClick={startBulkUpload}
                                                    className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-colors shadow-lg"
                                                >
                                                    <CloudUpload className="w-5 h-5" />
                                                    Upload & Process {uploadingFiles.length} CVs
                                                </button>
                                            )}

                                            {/* Upload Progress */}
                                            {uploadStatus === 'uploading' && (
                                                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                                    <div className="flex items-center gap-2">
                                                        <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                                                        <span className="text-sm font-medium text-blue-800">Uploading files...</span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Processing Progress */}
                                            {uploadStatus === 'processing' && (
                                                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                                                        <span className="text-sm font-medium text-amber-800">
                                                            Processing CVs... ({processingProgress.processed}/{processingProgress.total})
                                                        </span>
                                                    </div>
                                                    <div className="w-full bg-amber-200 rounded-full h-1.5">
                                                        <div
                                                            className="bg-amber-600 h-1.5 rounded-full transition-all"
                                                            style={{ width: `${processingProgress.total > 0 ? (processingProgress.processed / processingProgress.total) * 100 : 0}%` }}
                                                        />
                                                    </div>
                                                    <p className="text-xs text-amber-600 mt-2">
                                                        You can leave this page. Processing continues in background.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Completed Status */}
                                            {uploadStatus === 'completed' && (
                                                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-100">
                                                    <div className="flex items-center gap-2">
                                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                                        <span className="text-sm font-medium text-green-800">
                                                            Processing complete! {processingProgress.processed} CVs analyzed.
                                                            {processingProgress.failed > 0 && ` (${processingProgress.failed} failed)`}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Error Status */}
                                            {uploadStatus === 'error' && (
                                                <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-100">
                                                    <div className="flex items-center gap-2">
                                                        <AlertCircle className="w-4 h-4 text-red-600" />
                                                        <span className="text-sm font-medium text-red-800">Upload failed</span>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            setUploadStatus('idle');
                                                            setUploadProgress({});
                                                        }}
                                                        className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                                                    >
                                                        Try again
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Filter Controls Card */}
                                    {/* {selectedJobId && (
                                        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 flex-shrink-0 animate-fade-in">
                                            <div className="flex items-center gap-2 mb-4">
                                                <Filter className="w-4 h-4 text-slate-600" />
                                                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
                                                    Filter Candidates
                                                </h3>
                                            </div>

                                            <div className="space-y-4">
                                                
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-600 mb-2">
                                                        Search Candidates
                                                    </label>
                                                    <div className="relative">
                                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                        <input
                                                            type="text"
                                                            placeholder="Search name, role, skills..."
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-300 transition-colors"
                                                        />
                                                    </div>
                                                </div>

                                                
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-600 mb-2">
                                                        Education Tier
                                                    </label>
                                                    <select
                                                        value={maxEducationTier ?? ''}
                                                        onChange={(e) => setMaxEducationTier(e.target.value ? parseInt(e.target.value) : null)}
                                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-300 transition-colors bg-white"
                                                    >
                                                        <option value="">No Filter</option>
                                                        <option value="1">Tier 1 Only</option>
                                                        <option value="2">Tier 1-2</option>
                                                        <option value="3">Tier 1-3</option>
                                                    </select>
                                                </div>

                                                
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-600 mb-2">
                                                        Company Tier
                                                    </label>
                                                    <select
                                                        value={maxExperienceTier ?? ''}
                                                        onChange={(e) => setMaxExperienceTier(e.target.value ? parseInt(e.target.value) : null)}
                                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-300 transition-colors bg-white"
                                                    >
                                                        <option value="">No Filter</option>
                                                        <option value="1">Tier 1 Only</option>
                                                        <option value="2">Tier 1-2</option>
                                                        <option value="3">Tier 1-3</option>
                                                    </select>
                                                </div>

                                                
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-600 mb-2">
                                                        Min Years Experience
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        placeholder="e.g., 5"
                                                        value={minYearsExperience}
                                                        onChange={(e) => setMinYearsExperience(e.target.value)}
                                                        className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-300 transition-colors"
                                                    />
                                                </div>

                                                
                                                <button
                                                    onClick={() => {
                                                        setMaxEducationTier(null);
                                                        setMaxExperienceTier(null);
                                                        setMinYearsExperience('');
                                                    }}
                                                    className="w-full px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors"
                                                >
                                                    Clear All Filters
                                                </button>

                                                
                                                {(maxEducationTier !== null || maxExperienceTier !== null || minYearsExperience !== '') && (
                                                    <div className="pt-4 border-t border-slate-100">
                                                        <p className="text-xs font-medium text-slate-500 mb-2">Active Filters:</p>
                                                        <div className="flex flex-col gap-2">
                                                            {maxEducationTier !== null && (
                                                                <div className="flex items-center justify-between px-3 py-2 bg-blue-50 text-blue-700 text-xs font-medium rounded-lg border border-blue-100">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <GraduationCap className="w-3 h-3" />
                                                                        <span>Education ≤ Tier {maxEducationTier}</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => setMaxEducationTier(null)}
                                                                        className="hover:bg-blue-100 rounded-full p-0.5 transition-colors"
                                                                    >
                                                                        <X className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {maxExperienceTier !== null && (
                                                                <div className="flex items-center justify-between px-3 py-2 bg-purple-50 text-purple-700 text-xs font-medium rounded-lg border border-purple-100">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <Award className="w-3 h-3" />
                                                                        <span>Company ≤ Tier {maxExperienceTier}</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => setMaxExperienceTier(null)}
                                                                        className="hover:bg-purple-100 rounded-full p-0.5 transition-colors"
                                                                    >
                                                                        <X className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {minYearsExperience !== '' && (
                                                                <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg border border-emerald-100">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <Briefcase className="w-3 h-3" />
                                                                        <span>≥ {minYearsExperience} years</span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => setMinYearsExperience('')}
                                                                        className="hover:bg-emerald-100 rounded-full p-0.5 transition-colors"
                                                                    >
                                                                        <X className="w-3 h-3" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )} */}

                                    {/* Unlock Top 25% CTA - Desktop only (mobile version shown above) - PAYMENT_DISABLED: hidden when payments disabled */}
                                    {PAYMENT_ENABLED && selectedJobId && top25Data && top25Data.count > 0 && (
                                        <div className="hidden lg:block bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 rounded-2xl p-6 text-white shadow-lg animate-fade-in relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                                            <div className="relative">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Check className="w-5 h-5 text-yellow-300" />
                                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-200">Full Access</span>
                                                </div>
                                                <h3 className="text-lg font-bold mb-2">Unlock Top 25% Matches</h3>
                                                <p className="text-sm text-blue-100 mb-4">
                                                    Get instant access to {top25Data.count} top-ranked candidates' contact details, CVs, and LinkedIn profiles.
                                                </p>
                                                <button
                                                    onClick={() => setShowUnlockModal(true)}
                                                    disabled={isProcessingPurchase}
                                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-blue-700 font-bold rounded-xl hover:bg-blue-50 transition-colors shadow-lg disabled:opacity-50"
                                                >
                                                    {isProcessingPurchase ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <Unlock className="w-4 h-4" />
                                                    )}
                                                    Unlock Now
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Right Column: Candidates Results */}
                        <div className="flex-1 min-h-0 flex flex-col bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                            {/* Results Header */}
                            {selectedJob && !loading && !loadingCandidates && filteredCandidates.length > 0 && (
                                <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 flex-shrink-0 bg-white">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-slate-50 rounded-lg">
                                            <Users className="w-5 h-5 text-slate-600" />
                                        </div>
                                        <div>
                                            <h2 className="font-bold text-slate-900">
                                                {filteredCandidates.length} Candidate{filteredCandidates.length !== 1 ? 's' : ''}
                                            </h2>
                                            <p className="text-sm text-slate-500">
                                                Matched for {selectedJob.job_title}
                                            </p>
                                        </div>
                                    </div>
                                    {/* Clear All Button */}
                                    <button
                                        onClick={() => setShowClearAllModal(true)}
                                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        <span className="hidden sm:inline">Clear All</span>
                                    </button>
                                </div>
                            )}

                            {/* Candidates Table */}
                            {loading ? (
                                <CandidatesTableSkeleton />
                            ) : !selectedJobId ? (
                                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
                                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Users className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Select a Job Posting</h3>
                                    <p className="text-slate-600 max-w-md mx-auto">
                                        Choose a job posting from the left sidebar to view matched candidates.
                                    </p>
                                </div>
                            ) : loadingCandidates ? (
                                <CandidatesTableSkeleton />
                            ) : filteredCandidates.length === 0 ? (
                                <div className="p-12 text-center flex-1 flex flex-col items-center justify-center">
                                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                        <AlertCircle className="w-8 h-8 text-slate-400" />
                                    </div>
                                    <h3 className="text-xl font-semibold text-slate-900 mb-2">No Candidates Found</h3>
                                    <p className="text-slate-600 max-w-md mx-auto">
                                        {searchQuery
                                            ? 'No candidates match your search criteria. Try adjusting your filters.'
                                            : 'Upload candidates to view them all automatically ranked here.'}
                                    </p>
                                </div>
                            ) : (
                                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                                    {/* Table Header - Sticky - Hidden on mobile */}
                                    <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-4 bg-slate-50 border-b border-slate-100 text-sm font-semibold text-slate-600 flex-shrink-0">
                                        <div className="col-span-1 text-center flex items-center justify-center gap-1">
                                            Rank
                                            <button
                                                onClick={() => navigate('/how-matching-works')}
                                                className="p-0.5 hover:bg-slate-200 rounded-full transition-colors text-slate-400 hover:text-slate-600"
                                                title="How ranking works"
                                            >
                                                <Info className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        <div className="col-span-3">Candidate</div>
                                        <div className="col-span-3">Company</div>
                                        <div className="col-span-1 text-center">Exp.</div>
                                        <div className="col-span-2">Location</div>
                                        <div className="col-span-2 text-right">Actions</div>
                                    </div>

                                    {/* Table Body - Scrollable */}
                                    <div
                                        ref={tableContainerRef}
                                        className="divide-y divide-slate-100 flex-1 overflow-y-auto min-h-0"
                                    >
                                        {paginatedCandidates.map((candidate, index) => (
                                            <React.Fragment key={candidate.id}>
                                                {/* Desktop View (Table Row) */}
                                                <div
                                                    className="hidden lg:grid grid-cols-12 gap-4 px-6 py-5 items-center hover:bg-slate-50 transition-colors group"
                                                    style={{ animationDelay: `${index * 50}ms` }}
                                                >
                                                    {/* Ranking */}
                                                    <div className="col-span-1 flex flex-col items-center justify-center">
                                                        {candidate.match_rank === 1 ? (
                                                            <div className="flex flex-col items-center justify-center">
                                                                <Flame className="w-5 h-5 text-orange-500 mb-0.5" fill="currentColor" />
                                                                <div className="text-2xl font-black text-slate-900 leading-none drop-shadow-sm">1</div>
                                                            </div>
                                                        ) : (
                                                            <div className={`text-xl font-black leading-none ${candidate.match_rank === 2 ? 'text-slate-400' :
                                                                candidate.match_rank === 3 ? 'text-amber-700' : 'text-slate-300'
                                                                }`}>
                                                                #{candidate.match_rank}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Candidate Info */}
                                                    <div className="col-span-3 flex items-center gap-3">
                                                        <div className="relative">
                                                            <div className="w-11 h-11 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center text-slate-700 font-bold text-base flex-shrink-0">
                                                                {candidate.name.split(' ').map(n => n[0]).join('')}
                                                            </div>
                                                            {/* Purchase indicator badge */}
                                                            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${isCandidatePurchased(candidate.id) ? 'bg-emerald-500' : 'bg-slate-400'}`}>
                                                                {isCandidatePurchased(candidate.id) ? (
                                                                    <Check className="w-3.5 h-3.5 text-white" />
                                                                ) : (
                                                                    <Lock className="w-2.5 h-2.5 text-white" />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h4 className="font-semibold text-slate-900 truncate text-sm">
                                                                {isCandidatePurchased(candidate.id) ? candidate.name : censorName(candidate.name)}
                                                            </h4>
                                                            <p className="text-xs text-slate-500 truncate">{candidate.current_role}</p>
                                                        </div>
                                                    </div>

                                                    {/* Current Company */}
                                                    <div className="col-span-3">
                                                        <div className="flex items-center gap-2 text-slate-700">
                                                            <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                            <span className="text-sm truncate" title={candidate.current_company || 'Not specified'}>
                                                                {candidate.current_company || '—'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Experience */}
                                                    <div className="col-span-1 text-center">
                                                        <span className="text-sm font-medium text-slate-700">{candidate.years_experience}y</span>
                                                    </div>

                                                    {/* Location */}
                                                    <div className="col-span-2">
                                                        <div className="flex items-center gap-1.5 text-slate-700">
                                                            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                                            <span className="text-sm truncate" title={candidate.city ? `${candidate.city}, ${candidate.location}` : candidate.location}>
                                                                {candidate.location}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="col-span-2 flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => setSelectedCandidate(candidate)}
                                                            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg transition-colors"
                                                        >
                                                            View Profile
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteCandidate(candidate.id);
                                                            }}
                                                            disabled={deletingCandidateId === candidate.id}
                                                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                                            title="Delete candidate"
                                                        >
                                                            {deletingCandidateId === candidate.id ? (
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                            ) : (
                                                                <Trash2 className="w-4 h-4" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Mobile View (Card) */}
                                                <div
                                                    className="lg:hidden p-4 hover:bg-slate-50 transition-colors"
                                                    onClick={() => setSelectedCandidate(candidate)}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className="relative">
                                                            <div className="w-12 h-12 bg-gradient-to-br from-slate-200 to-slate-300 rounded-xl flex items-center justify-center text-slate-700 font-bold text-lg flex-shrink-0 shadow-sm">
                                                                {candidate.name.split(' ').map(n => n[0]).join('')}
                                                            </div>
                                                            {/* Purchase indicator badge */}
                                                            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${isCandidatePurchased(candidate.id) ? 'bg-emerald-500' : 'bg-slate-400'}`}>
                                                                {isCandidatePurchased(candidate.id) ? (
                                                                    <Check className="w-3.5 h-3.5 text-white" />
                                                                ) : (
                                                                    <Lock className="w-2.5 h-2.5 text-white" />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-start justify-between gap-2">
                                                                <h4 className="font-bold text-slate-900 truncate">
                                                                    {isCandidatePurchased(candidate.id) ? candidate.name : censorName(candidate.name)}
                                                                </h4>
                                                                <span className="text-lg font-black text-slate-900 bg-slate-50 px-2 py-0.5 rounded-lg border border-slate-100">
                                                                    #{candidate.match_rank}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm font-medium text-slate-600 truncate">{candidate.current_role}</p>
                                                            {candidate.current_company && (
                                                                <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                                                                    <Building2 className="w-3 h-3" />
                                                                    {candidate.current_company}
                                                                </p>
                                                            )}

                                                            <div className="mt-3 flex flex-wrap gap-2">
                                                                <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded-lg text-[10px] font-bold text-slate-600 uppercase tracking-tight">
                                                                    <Briefcase className="w-3 h-3" />
                                                                    {candidate.years_experience} Yrs
                                                                </div>
                                                                <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-100 rounded-lg text-[10px] font-bold text-slate-600 uppercase tracking-tight">
                                                                    <MapPin className="w-3 h-3" />
                                                                    {candidate.location}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col items-center gap-2">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteCandidate(candidate.id);
                                                                }}
                                                                disabled={deletingCandidateId === candidate.id}
                                                                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                                                title="Delete candidate"
                                                            >
                                                                {deletingCandidateId === candidate.id ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <Trash2 className="w-4 h-4" />
                                                                )}
                                                            </button>
                                                            <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                    </div>

                                    {/* Table Footer with Pagination */}
                                    <div className="px-4 py-4 md:px-6 md:py-4 bg-slate-50 border-t border-slate-100 flex flex-col md:flex-row items-center justify-between gap-4 text-sm flex-shrink-0">
                                        <div className="text-slate-600 order-2 md:order-1">
                                            Showing <span className="font-semibold">{Math.min(filteredCandidates.length, (currentPage - 1) * ITEMS_PER_PAGE + 1)}</span> to{' '}
                                            <span className="font-semibold">{Math.min(filteredCandidates.length, currentPage * ITEMS_PER_PAGE)}</span> of{' '}
                                            <span className="font-semibold">{filteredCandidates.length}</span>
                                        </div>

                                        <div className="flex items-center gap-2 order-1 md:order-2 w-full md:w-auto justify-between md:justify-end">
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                                disabled={currentPage === 1}
                                                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white shadow-sm"
                                            >
                                                <ChevronLeft className="w-5 h-5 text-slate-600" />
                                            </button>
                                            <span className="text-slate-600 font-bold px-4 bg-white py-2 rounded-lg border border-slate-100 shadow-sm">
                                                Page {currentPage} of {totalPages || 1}
                                            </span>
                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                                disabled={currentPage === totalPages || totalPages === 0}
                                                className="p-2 border border-slate-200 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors bg-white shadow-sm"
                                            >
                                                <ChevronRight className="w-5 h-5 text-slate-600" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div >

            {/* Unlock Top 25% Modal - PAYMENT_DISABLED: hidden when payments disabled */}
            {
                PAYMENT_ENABLED && showUnlockModal && top25Data && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setShowUnlockModal(false)}>
                        <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-scale-in" onClick={(e) => e.stopPropagation()}>
                            {/* Header */}
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-8 text-white">
                                <div className="flex items-center gap-2 mb-2">
                                    <Check className="w-5 h-5 text-yellow-300" />
                                    <span className="text-xs font-bold uppercase tracking-wider text-blue-200">Full Access</span>
                                </div>
                                <h2 className="text-2xl font-bold mb-1">Unlock Top 25% Candidates</h2>
                                <p className="text-blue-100 text-sm">Get full access to the highest-ranked matches</p>
                            </div>

                            {/* Content */}
                            <div className="p-6">
                                <div className="space-y-4 mb-6">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-semibold text-slate-900">Full Contact Details</p>
                                            <p className="text-sm text-slate-600">Names, email addresses, and direct contact info</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-semibold text-slate-900">CV Downloads</p>
                                            <p className="text-sm text-slate-600">Full CV access for detailed review</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-semibold text-slate-900">LinkedIn Profiles</p>
                                            <p className="text-sm text-slate-600">Direct links to professional profiles</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="font-semibold text-slate-900">Candidate Notifications</p>
                                            <p className="text-sm text-slate-600">Candidates are notified they've been shortlisted</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Price Summary */}
                                <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-slate-700">Candidates included</span>
                                        <span className="font-bold text-slate-900">{top25Data.count}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-slate-700">Total price</span>
                                        <span className="text-2xl font-bold text-slate-900">£199</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => setShowUnlockModal(false)}
                                        className="flex-1 px-4 py-3 text-slate-600 font-semibold border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowUnlockModal(false);
                                            handlePurchase('BULK', top25Data.candidateIds);
                                        }}
                                        disabled={isProcessingPurchase}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg disabled:opacity-50"
                                    >
                                        {isProcessingPurchase ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Check className="w-4 h-4" />
                                        )}
                                        Proceed to Payment
                                    </button>
                                </div>
                            </div>

                            {/* Close Button */}
                            <button
                                onClick={() => setShowUnlockModal(false)}
                                className="absolute top-4 right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                )
            }

            {/* Candidate Detail Modal */}
            {
                selectedCandidate && (
                    <CandidateDetailModal
                        candidate={selectedCandidate}
                        onClose={() => setSelectedCandidate(null)}
                        jobTitle={selectedJob?.job_title}
                        isPurchased={isCandidatePurchased(selectedCandidate.id)}
                        onPurchase={(candidateId, candidateName) => handlePurchase('SINGLE', [candidateId], candidateName)}
                        censorName={censorName}
                    />
                )
            }

            {/* Clear All Candidates Confirmation Modal */}
            {showClearAllModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
                    onClick={() => !isClearingAll && setShowClearAllModal(false)}
                >
                    <div
                        className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-scale-in"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-6 text-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <Trash2 className="w-6 h-6" />
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold">Clear All Candidates</h2>
                                    <p className="text-red-100 text-sm">This action cannot be undone</p>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            <p className="text-slate-600 mb-6">
                                Are you sure you want to delete all <span className="font-semibold text-slate-900">{candidates.length} candidate{candidates.length !== 1 ? 's' : ''}</span> for this job posting?
                                All uploaded CVs and match data will be permanently removed.
                            </p>

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowClearAllModal(false)}
                                    disabled={isClearingAll}
                                    className="flex-1 px-4 py-3 text-slate-600 font-semibold border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleClearAllCandidates}
                                    disabled={isClearingAll}
                                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all shadow-lg disabled:opacity-50"
                                >
                                    {isClearingAll ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 className="w-4 h-4" />
                                            Delete All
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Close Button */}
                        {!isClearingAll && (
                            <button
                                onClick={() => setShowClearAllModal(false)}
                                className="absolute top-4 right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div >
    );
};

